import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScanTypeSelector } from '@/components/scan/scan-type-selector';
import { ScanProgress } from '@/components/scan/scan-progress';
import { DomainGateBanner } from '@/components/domain/domain-gate-banner';
import { Play, CheckCircle2, AlertTriangle, RotateCcw } from 'lucide-react';
import { SCAN_STEP_MESSAGES, type ScanType } from '@nirvik/shared';
import { Link } from 'react-router-dom';

export function NewScanPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();

  // Endpoint/hybrid projects must verify domain ownership before scanning.
  const { data: projectRes } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId!, portfolioId!, projectId!),
    enabled: !!orgId && !!portfolioId && !!projectId,
  });
  const project = projectRes?.data as
    | { projectType?: string; domainVerified?: boolean; verifiableHost?: string | null }
    | undefined;
  const domainBlocked = project?.projectType !== undefined && project.projectType !== 'repo' && project.domainVerified === false;
  const [scanType, setScanType] = useState<ScanType>('full');
  const [step, setStep] = useState<'select' | 'progress' | 'done' | 'error'>('select');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [scanId, setScanId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startMutation = useMutation({
    mutationFn: () => apiClient.startScan(orgId!, portfolioId!, projectId!, scanType),
    onSuccess: (res) => {
      const scan = res.data as { scanId: string; status: string } | undefined;
      if (scan?.scanId) {
        setScanId(scan.scanId);
        setStep('progress');
        setProgress(0);
        const messages = SCAN_STEP_MESSAGES[scanType] ?? SCAN_STEP_MESSAGES.full;
        setMessage(messages[0]);
        startPolling(scan.scanId, messages);
      }
    },
    onError: (err: Error) => {
      setErrorMessage(err.message);
      setStep('error');
    },
  });

  const startPolling = useCallback(
    (sid: string, messages: string[]) => {
      clearPoll();
      pollRef.current = setInterval(async () => {
        try {
          const res = await apiClient.getScan(orgId!, portfolioId!, projectId!, sid);
          const scan = res.data as {
            status: string;
            progress?: number;
            currentStep?: string;
          } | undefined;
          if (!scan) return;

          const pct = scan.progress ?? 0;
          setProgress(pct);

          if (scan.currentStep) {
            setMessage(scan.currentStep);
          } else {
            const msgIndex = Math.floor((pct / 100) * messages.length);
            setMessage(messages[Math.min(msgIndex, messages.length - 1)]);
          }

          if (scan.status === 'completed') {
            setProgress(100);
            clearPoll();
            setStep('done');
          } else if (scan.status === 'failed') {
            clearPoll();
            setErrorMessage('The scan encountered an error and could not complete.');
            setStep('error');
          } else if (scan.status === 'cancelled') {
            clearPoll();
            setStep('select');
          }
        } catch {
          // ignore transient poll errors
        }
      }, 3000);
    },
    [orgId, portfolioId, projectId, clearPoll],
  );

  const cancelScan = useCallback(() => {
    clearPoll();
    if (scanId) {
      apiClient.cancelScan(orgId!, portfolioId!, projectId!, scanId).catch(() => {});
    }
    setStep('select');
    setProgress(0);
  }, [clearPoll, scanId, orgId, portfolioId, projectId]);

  useEffect(() => {
    return () => clearPoll();
  }, [clearPoll]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Start a New Scan</h1>
        <p className="text-muted-foreground dark:text-muted-foreground mt-1">
          {step === 'select'
            ? 'Choose what type of security scan to run on your site.'
            : step === 'progress'
              ? 'Your scan is in progress. This may take a few minutes.'
              : step === 'done'
                ? 'Your scan has completed successfully.'
                : 'Something went wrong with the scan.'}
        </p>
      </div>

      {step === 'select' && domainBlocked && (
        <DomainGateBanner
          orgId={orgId!} portfolioId={portfolioId!} projectId={projectId!}
          domain={project?.verifiableHost ?? undefined}
        />
      )}

      {step === 'select' && (
        <div className="space-y-6">
          <ScanTypeSelector value={scanType} onChange={setScanType} />

          <div className="flex justify-end">
            <Button
              size="lg"
              onClick={() => startMutation.mutate()}
              disabled={startMutation.isPending || domainBlocked}
            >
              <Play className="h-4 w-4 mr-2" />
              {startMutation.isPending ? 'Starting...' : 'Start Scan'}
            </Button>
          </div>
        </div>
      )}

      {step === 'progress' && (
        <Card className="p-6">
          <ScanProgress progress={progress} message={message} onCancel={cancelScan} />
        </Card>
      )}

      {step === 'done' && scanId && (
        <Card className="p-6">
          <div className="flex flex-col items-center gap-4 py-8">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground">Scan Complete</h2>
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">Your security scan has finished. View the results below.</p>
            <Link to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/${scanId}`}>
              <Button size="lg">View Results</Button>
            </Link>
          </div>
        </Card>
      )}

      {step === 'error' && (
        <Card className="p-6">
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertTriangle className="h-16 w-16 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground">Scan Failed</h2>
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">{errorMessage}</p>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                setStep('select');
                setProgress(0);
                setErrorMessage('');
              }}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
